import {useEffect, useState, useCallback} from 'react';
import { Grid, Snackbar, Alert } from '@mui/material';
import { shuffleDataArray } from '../../hooks/dataHooks';
import SingleNotesPaginatedCard from '../SingleNotesPaginatedCard';
import PitchTestListener from '../PitchTestListener';
import PitchTestEndModal from '../PitchTestEndModal';
import DonationPrompt from '../DonationPrompt';
import { useIsTinyMobileScreen, useIsSmallScreen } from '../../hooks/screenSizeHooks';
import { correctAnswersResponse } from '../../utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAnime } from '../../hooks/AnimeProvider';


const PitchTestSession = (props) => {
  const {pitchTestLevelData, levelIndex, setLevelIndex, isLastLevel, NumQuestionsPerLevel} = props;
  const isTinyMobileScreen = useIsTinyMobileScreen();
  const isSmallScreen = useIsSmallScreen();
  const {handleTriggerAnime} = useAnime();
  const [questionArray, setQuestionArray] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [clueSeenArray, setClueSeenArray] = useState([]);
  const [skippedNotesArray, setSkippedNotesArray] = useState([]);
  const [showEndLevelModal, setShowEndLevelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isSideBySide = useMediaQuery('(min-width:900px)');

  const setLevelInitialQuestions = useCallback(() => {
    setQuestionArray([]);
    setClueSeenArray([]);
    setSkippedNotesArray([]);
    setQuestionIndex(0);
    const shuffled = shuffleDataArray(pitchTestLevelData.pitchTestQuestions);
    setQuestionArray(shuffled.slice(0, NumQuestionsPerLevel));

  }, [pitchTestLevelData, NumQuestionsPerLevel])
 
  // everytime the level changes, reset everything and shuffled the quesitons
  useEffect(() => {
    setLevelInitialQuestions();
  }, [pitchTestLevelData, levelIndex, setLevelInitialQuestions]);

  const handleQuestionCorrect = () => {
    // 1. trigger snackerbar for 2 seconds
    setShowSnackbar(true);
    handleTriggerAnime();
    setIsLoading(true);
    
    // 2. wait 2 seconds and go to next question
    if(questionIndex + 1 >= questionArray.length) {
      setTimeout(() => {     
        setShowEndLevelModal(true);
        setIsLoading(false)
      }, 2000);
    } else {
      setTimeout(() => {
        setQuestionIndex(questionIndex + 1);
        setIsLoading(false);
      }, 2000);
    }
  }

  const openClueClicked = (question) => {
    setClueSeenArray([...clueSeenArray, question]);
  }

  const skipNoteClicked = (question) => {
    setSkippedNotesArray([...skippedNotesArray, question]);

    if(questionIndex + 1 >= questionArray.length) {
      setShowEndLevelModal(true);  
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setQuestionIndex(questionIndex + 1);
        setIsLoading(false);
      }, 2000);
    }
      
  }

  const proceedNextLevel = () => {
    setLevelIndex(levelIndex + 1);
    setShowEndLevelModal(false);
  }

  const redoSameLevel = () => {
    setLevelInitialQuestions();
    setShowEndLevelModal(false);
  }

  return (
    <>
      <Snackbar
          anchorOrigin={{vertical: isSmallScreen ? 'bottom' : 'top', horizontal: 'right'}}
          open={showSnackbar}
          autoHideDuration={2000}
          onClose={()=> setShowSnackbar(false)}          
          >
          <Alert
            icon={false}
            severity='success'
            variant="filled"
            sx={{ width: '100%',   
              display: 'block',
              textAlign: "center"}}
            style={{borderRadius: '10px'}}
          >
            <img 
              src="https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExeDV5aTl3ZDBpMzk5bHJ3NXJpZGVqejFkZDVwZTk1anowbWkxczdyNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/5mUGrtcKCjKXE8Vsef/giphy.gif" 
              style={{maxHeight: '30px', marginRight: '10px'}}
              alt="Correct Celebration Gif"
            />
            {correctAnswersResponse[Math.floor(Math.random() * correctAnswersResponse.length)]}
            <img 
              src="https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExeDV5aTl3ZDBpMzk5bHJ3NXJpZGVqejFkZDVwZTk1anowbWkxczdyNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/5mUGrtcKCjKXE8Vsef/giphy.gif" 
              style={{maxHeight: '30px', marginLeft: '10px'}}
              alt="Correct Celebration Gif"
            />   
          </Alert>
        </Snackbar>
      {!!questionArray.length && 
        <Grid container direction='row' spacing={3} justifyContent="space-around">
          {/**Left Side */}
          <Grid  item xs={12} sm={8} md={5}>
            <SingleNotesPaginatedCard 
                isTinyMobileScreen={isTinyMobileScreen}
                isSmallScreen={isSmallScreen}
                imgUrl={questionArray[questionIndex].noteImg}
                imgTitle={`note ${questionArray[questionIndex].noteBaseName}${questionArray[questionIndex].accidental}`}
                paginationLengthTotal={questionArray.length}
                paginationIndex={questionIndex + 1}
                isLoading={isLoading}
              />
            {isSideBySide && <DonationPrompt />}
          </Grid>
          {/**Right Side */}
          <Grid item xs={12} sm={8} md={5} lg={6}>
            <PitchTestListener 
              question={questionArray[questionIndex]} 
              questionIndex={questionIndex}
              handleQuestionCorrect={handleQuestionCorrect} 
              openClueClicked={openClueClicked}
              isSmallScreen={isSmallScreen}
              skipNoteClicked={skipNoteClicked}
              showEndLevelModal={showEndLevelModal}
            />
            {!isSideBySide && <DonationPrompt />}
          </Grid>
        </Grid>  
      }
      {!!showEndLevelModal &&
        <PitchTestEndModal
          showEndLevelModal={showEndLevelModal}
          pitchTestLevelData={pitchTestLevelData}
          clueSeenArray={clueSeenArray}
          skippedNotesArray={skippedNotesArray}
          questionArray={questionArray}
          proceedNextLevel={proceedNextLevel}
          redoSameLevel={redoSameLevel}
          isLastLevel={isLastLevel}
        />
      }
    </>
  )
}

export default PitchTestSession;